import { Locale } from "../Models/Localization/Locale";
import { LocalizedTextDto } from "../Models/LocalizedTextDto";
import { ResourceProvider } from "../Resources/StaticResources";

export interface PasswordCustomCriterias {
    customPasswordRuleMessage?: LocalizedTextDto;
    customAdminPasswordRuleMessage?: LocalizedTextDto;
    customPasswordValidationRegex?: string;
}

export class PasswordCriteriaHelper {
    public static GetPasswordInstruction(
        clinicCustomCriterias: PasswordCustomCriterias,
        userHasAdminPermission: boolean,
        isNewAdminUser: boolean,
        stringProvider: ResourceProvider
    ): string {
        if (userHasAdminPermission && clinicCustomCriterias.customAdminPasswordRuleMessage) {
            const newAdminRightsMessage = isNewAdminUser ? `${PasswordCriteriaHelper.getString("NewAdminRightsMessage", stringProvider)} ` : "";
            return `${newAdminRightsMessage}${PasswordCriteriaHelper.GetLocalizedText(clinicCustomCriterias.customAdminPasswordRuleMessage)}`;
        }

        if (clinicCustomCriterias.customPasswordRuleMessage) {
            return PasswordCriteriaHelper.GetLocalizedText(clinicCustomCriterias.customPasswordRuleMessage);
        }

        if (clinicCustomCriterias.customPasswordValidationRegex) {
            const generatedMessage = PasswordCriteriaHelper.parseRegexAndBuildCustomMessage(
                clinicCustomCriterias.customPasswordValidationRegex,
                userHasAdminPermission,
                stringProvider
            );
            if (generatedMessage) return generatedMessage;
        }

        if (userHasAdminPermission) {
            return PasswordCriteriaHelper.getString(
                isNewAdminUser
                    ? "NewAdminUserPasswordResetLabel"
                    : "PasswordSecurityAdminDescription",
                stringProvider
            );
        }
        return PasswordCriteriaHelper.getString("PasswordSecurityDescription", stringProvider);
    }

    private static GetLocalizedText(dto: LocalizedTextDto): string {
        switch (sessionStore.locale) {
            case Locale.fr: return dto.Fr;
            case Locale.es: return dto.Es;
            default: return dto.En;
        }
    }

    private static parseRegexAndBuildCustomMessage(passwordValidationRegex: string, hasAdminPermission: boolean, stringProvider: ResourceProvider): string | null {
        const regex = new RegExp(passwordValidationRegex);

        const lengthRegex: RegExp = /\{\d+,\d+\}/;
        const uppercasePattern = "[A-Z]";
        const lowercasePattern = "[a-z]";
        const digitPattern = "[\\d]";
        const specialCharPattern = "[\\W_]";

        const searchLengthInRegex = lengthRegex.exec(passwordValidationRegex);
        if (!searchLengthInRegex) {
            return;
        }

        const match = searchLengthInRegex[0]?.replace("{", "").replace("}", "").split(",");
        let labelList = [];

        if (lengthRegex.test(passwordValidationRegex) && match.any() && match.length == 2) {
            const minLenght = parseInt(match[0]);
            labelList.push(PasswordCriteriaHelper.getString("PasswordCriteriaLengthLabel", stringProvider, [minLenght]));
        }
        else {
            labelList.push(PasswordCriteriaHelper.getString("PasswordCriteriaLengthLabel", stringProvider, [(hasAdminPermission ? 16 : 8)]));
        }

        if (PasswordCriteriaHelper.regexHasPattern(regex, uppercasePattern)) {
            labelList.push(PasswordCriteriaHelper.getString("PasswordCriteriaUppercaseLabel", stringProvider));
        }

        if (PasswordCriteriaHelper.regexHasPattern(regex, lowercasePattern)) {
            labelList.push(PasswordCriteriaHelper.getString("PasswordCriteriaLowercaseLabel", stringProvider));
        }

        if (PasswordCriteriaHelper.regexHasPattern(regex, specialCharPattern)) {
            labelList.push(PasswordCriteriaHelper.getString("PasswordCriteriaSpecialCharacterLabel", stringProvider));
        }

        if (PasswordCriteriaHelper.regexHasPattern(regex, digitPattern)) {
            labelList.push(PasswordCriteriaHelper.getString("PasswordCriteriaNumericLabel", stringProvider));
        }

        if (labelList.length < 2)
            return;

        let message = "";
        for (let index = 0; index < labelList.length; index++) {
            if (index == 0) {
                message = labelList[0];
            }
            else if (index == 1) {
                message += ` ${labelList[1]}`;
            }
            else if (index == labelList.length - 1) {
                message += ` ${PasswordCriteriaHelper.getString("AndLabel", stringProvider).toLowerCase()} ${labelList[index]}.`;
            }
            else {
                message += `, ${labelList[index]}`;
            }
        }
        return message;
    }

    private static regexHasPattern(regex: RegExp, subpattern: string): boolean {
        const regexString = regex.toString();
        return regexString.includes(subpattern);
    }

    private static getString(label: string, stringProvider: ResourceProvider | null, tokens?: object): string {
        return stringProvider
            ? stringProvider.GetString(label, tokens)
            : cacheStore?.GetString(label, tokens);
    }
}
